/**
 * MainTeamTab — Phase 6 Plan 01 Task 3 (MGR-01 / MGR-04).
 *
 * The Sales-Manager "Team" tab: a sortable pipeline-by-rep table. One row per
 * rep the signed-in manager can see (SF sharing rules scope the rows server-side
 * via the manager's own token — MGR-04). Clicking a rep name drills to that
 * rep's records in Salesforce via the frontdoor.jsp bridge in a new tab (D-07).
 *
 * A light extension of the MainTableTile pattern: it keeps the Card chrome, the
 * UX-07 4-state blocks (loading Skeleton / cache-warming / SF-unreachable /
 * success), the `text-[0.72rem]` uppercase column headers, the em-dash null
 * convention, and the footer total — but adds sortable headers (▲/▼ glyphs, no
 * icon package) and a window.open frontdoor click (reused from MainTreemapTile),
 * which the base MainTableTile does not support.
 *
 * Columns per UI-SPEC: REP · SEGMENT · OPEN PIPELINE · # OPPS · WEIGHTED FCST.
 * SEGMENT and WEIGHTED FCST have no data source in this walking-skeleton slice,
 * so they render an honest em-dash (never a fabricated value). A COVERAGE column
 * is deliberately deferred to Plan 02 (see the slot comment below) — no
 * fabricated-zero coverage here (D-13 / Pitfall 5).
 */
import { useState } from 'react';

import {
  CacheNotReadyError,
  SalesforceUnavailableError,
} from '../../lib/api-client.js';
import { formatAmount } from '../../lib/format.js';
import {
  useTeamPipeline,
  useTeamForecastByCategory,
  useTeamWinrateByRep,
  useTeamCycleAcvByRep,
  type TeamPipelineByRepRow,
} from '../../queries/useTeamPipeline.js';
import { SfUnreachableBanner } from '../SfUnreachableBanner.js';
import { Card, CardHeader, CardTitle } from '../ui/Card.js';
import { Skeleton } from '../ui/Skeleton.js';

import { MainCoverageCell } from './MainCoverageCell.js';
import { MainTableTile, type TableColumn } from './MainTableTile.js';

import type { MainGroupedResponse } from '@saledash/shared';

/**
 * Per-rep coverage fields attached by the Plan 02 route (team.ts). Read off the
 * pipeline-by-rep row here (kept local so the Plan 01 useTeamPipeline hook shape
 * stays untouched) — the route always returns them; older/mocked rows without
 * them fall back to the honest no-quota state.
 */
interface CoverageFields {
  pctOfTarget?: number | null;
  noQuota?: boolean;
}

interface MainTeamTabProps {
  /** Caller passes activeTab === 'team' so the query is lazy (RESEARCH Pitfall 4). */
  active: boolean;
}

/** Columns the pipeline-by-rep SOQL actually feeds are sortable. */
type SortKey = 'ownerName' | 'openAmount' | 'oppCount';
type SortDir = 'asc' | 'desc';

/**
 * Build the absolute /api/sf/link/:id href for the frontdoor bridge. Mirrors
 * MainTreemapTile.sfLinkHref: on the production host the api lives on
 * api.salesdashboard.adeptia.org (the SPA is cross-origin on cro.*); locally
 * it's a same-origin relative path.
 */
function sfLinkHref(recordId: string): string {
  const apiBase =
    typeof window !== 'undefined' &&
    window.location.hostname.endsWith('salesdashboard.adeptia.org')
      ? `${window.location.protocol}//api.salesdashboard.adeptia.org`
      : '';
  return `${apiBase}/api/sf/link/${recordId}`;
}

const EM_DASH = '—';

function openInSalesforce(recordId: string): void {
  if (recordId && typeof window !== 'undefined') {
    window.open(sfLinkHref(recordId), '_blank', 'noopener,noreferrer');
  }
}

function sortRows(
  rows: TeamPipelineByRepRow[],
  key: SortKey,
  dir: SortDir,
): TeamPipelineByRepRow[] {
  const sorted = [...rows].sort((a, b) => {
    if (key === 'ownerName') {
      return a.ownerName.localeCompare(b.ownerName);
    }
    const av = key === 'openAmount' ? a.openAmount ?? 0 : a.oppCount;
    const bv = key === 'openAmount' ? b.openAmount ?? 0 : b.oppCount;
    return av - bv;
  });
  return dir === 'asc' ? sorted : sorted.reverse();
}

interface SortableHeaderProps {
  label: string;
  columnKey: SortKey;
  activeKey: SortKey;
  dir: SortDir;
  numeric?: boolean;
  onSort: (key: SortKey) => void;
}

function SortableHeader({
  label,
  columnKey,
  activeKey,
  dir,
  numeric,
  onSort,
}: SortableHeaderProps): React.JSX.Element {
  const isActive = activeKey === columnKey;
  const glyph = isActive ? (dir === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th
      className={`px-4 py-2 text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500 ${numeric ? 'text-right' : 'text-left'}`}
    >
      <button
        type="button"
        onClick={() => { onSort(columnKey); }}
        className="uppercase tracking-wider hover:text-brand-600"
        aria-label={`Sort by ${label}`}
      >
        {label}
        {isActive && <span className="text-ink-500">{glyph}</span>}
      </button>
    </th>
  );
}

export function MainTeamTab({ active }: MainTeamTabProps): React.JSX.Element {
  const { data, isLoading, isError, error, refetch } = useTeamPipeline({ active });

  // Local sort state — default rep name ascending.
  const [sortKey, setSortKey] = useState<SortKey>('ownerName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  function handleSort(key: SortKey): void {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const title = 'Pipeline by Rep';

  const header = (
    <CardHeader>
      <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
        {title}
      </CardTitle>
    </CardHeader>
  );

  // The pipeline-by-rep card renders through its own UX-07 4-state machine; it is
  // built into `pipelineCard` (rather than early-returned) so the MGR-02/MGR-03
  // drilldown tiles below it always render regardless of the pipeline state.
  let pipelineCard: React.JSX.Element;
  if (isError && error instanceof CacheNotReadyError) {
    // UX-07: cache-warming (rare for live-SOQL, kept for family consistency).
    pipelineCard = (
      <Card data-testid="main-team-tab">
        {header}
        <div className="p-4">
          <div
            role="status"
            className="flex items-start gap-3 rounded-lg border border-warn bg-warn-bg px-4 py-3 text-sm text-warn-text"
            data-testid="cache-warming-banner"
          >
            <span
              aria-hidden
              className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-warn text-xs font-bold text-white"
            >
              ⌛
            </span>
            <div className="flex-1">
              <p className="font-semibold">Cache warming up</p>
              <p className="mt-1 text-xs text-warn-text/80">
                First data refresh expected within ~7 minutes.
              </p>
            </div>
          </div>
        </div>
      </Card>
    );
  } else if (isError && error instanceof SalesforceUnavailableError) {
    // UX-07: SF-unreachable (also the surface for 401 NO_VAULT_ROW).
    pipelineCard = (
      <Card data-testid="main-team-tab">
        {header}
        <div className="p-4">
          <SfUnreachableBanner onRetry={() => { void refetch(); }} />
        </div>
      </Card>
    );
  } else if (isLoading || !data) {
    // Loading
    pipelineCard = (
      <Card data-testid="main-team-tab">
        {header}
        <div className="p-4 space-y-3" aria-busy="true">
          <Skeleton className="h-4 w-full" data-testid="skeleton-row-1" />
          <Skeleton className="h-4 w-full" data-testid="skeleton-row-2" />
          <Skeleton className="h-4 w-full" data-testid="skeleton-row-3" />
        </div>
      </Card>
    );
  } else {
    const rows = data.rows;
    const sortedRows = sortRows(rows, sortKey, sortDir);
    const totalOpen = rows.reduce((acc, r) => acc + (r.openAmount ?? 0), 0);
    const totalOpps = rows.reduce((acc, r) => acc + r.oppCount, 0);

    pipelineCard = (
      <Card data-testid="main-team-tab">
        {header}

        {/* Empty state — no team rows (SF sharing rules returned nothing). Never a
            fabricated zero (Pitfall 5). Copy verbatim from UI-SPEC. */}
        {rows.length === 0 && (
          <p
            className="py-12 text-center text-sm text-ink-500"
            data-testid="team-empty-state"
          >
            No reps in view — you&apos;re seeing only your own team, per Salesforce
            sharing rules. If this looks empty, your role hierarchy may have no
            reports.
          </p>
        )}

        {/* Pipeline-by-rep table */}
        {rows.length > 0 && (
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b border-ink-200">
                <tr>
                  <SortableHeader
                    label="REP"
                    columnKey="ownerName"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <th className="px-4 py-2 text-left text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                    SEGMENT
                  </th>
                  <SortableHeader
                    label="OPEN PIPELINE"
                    columnKey="openAmount"
                    activeKey={sortKey}
                    dir={sortDir}
                    numeric
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="# OPPS"
                    columnKey="oppCount"
                    activeKey={sortKey}
                    dir={sortDir}
                    numeric
                    onSort={handleSort}
                  />
                  <th className="px-4 py-2 text-right text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                    WEIGHTED FCST
                  </th>
                  {/* COVERAGE column (Plan 02, D-11) — per-rep open pipeline vs 3×
                      remaining-quota target; honest "No quota defined" when absent. */}
                  <th className="px-4 py-2 text-left text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                    COVERAGE
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr
                    key={row.OwnerId}
                    className="min-h-9 hover:bg-ink-100 border-b border-ink-100"
                    data-testid="team-data-row"
                  >
                    {/* REP — frontdoor deep-link (brand-600 + ↗), new tab. */}
                    <td className="px-4 py-3" data-testid="team-rep-cell">
                      <button
                        type="button"
                        onClick={() => { openInSalesforce(row.OwnerId); }}
                        className="text-brand-600 hover:text-brand-700 hover:underline"
                      >
                        {row.ownerName} ↗
                      </button>
                    </td>
                    {/* SEGMENT — no segment field on this org → honest em-dash. */}
                    <td className="px-4 py-3 text-ink-900">{EM_DASH}</td>
                    {/* OPEN PIPELINE */}
                    <td className="px-4 py-3 text-ink-900 tabular-nums text-right">
                      {row.openAmount === null ? EM_DASH : formatAmount(row.openAmount)}
                    </td>
                    {/* # OPPS */}
                    <td className="px-4 py-3 text-ink-900 tabular-nums text-right">
                      {row.oppCount}
                    </td>
                    {/* WEIGHTED FCST — no data source yet → honest em-dash. */}
                    <td className="px-4 py-3 text-ink-900 tabular-nums text-right">
                      {EM_DASH}
                    </td>
                    {/* COVERAGE — per-rep 3× coverage chip / honest no-quota state (D-11/D-13). */}
                    <td className="px-4 py-3 text-left" data-testid="team-coverage-cell">
                      <MainCoverageCell
                        pctOfTarget={(row as TeamPipelineByRepRow & CoverageFields).pctOfTarget ?? null}
                        noQuota={(row as TeamPipelineByRepRow & CoverageFields).noQuota ?? false}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr
                  className="border-t-2 border-ink-200 font-semibold bg-white"
                  data-testid="team-footer-row"
                >
                  <td className="px-4 py-3 text-ink-900 font-bold">TOTAL</td>
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3 tabular-nums text-right text-ink-900">
                    {formatAmount(totalOpen)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-right text-ink-900">
                    {totalOpps}
                  </td>
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3" />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="main-team-panel">
      {pipelineCard}
      {/* MGR-02 + MGR-03 drilldowns packed below the pipeline table. Both are lazy
          (enabled only when the Team tab is active) and sharing-scoped via the same
          manager-token path. */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <TeamForecastByCategoryTile active={active} />
        <TeamPerformanceTile active={active} />
      </div>
    </div>
  );
}

// ─── MGR-02: Forecast-by-category tile ───────────────────────────────────────
// Canonical display order for the custom Forecast_Position__c picklist. Any value
// not in this list (defensive) sorts after these; the null-position row is always
// last, labeled "(no position)".
const FORECAST_POSITION_ORDER = ['Commit', 'Best Case', 'Most Likely', 'Pipeline'];
const NO_POSITION_LABEL = '(no position)';

const FORECAST_COLUMNS: TableColumn[] = [
  { key: 'label', header: 'FORECAST POSITION' },
  { key: 'value', header: 'OPEN PIPELINE', isNumeric: true },
  { key: 'count', header: '# OPPS', isNumeric: true },
];

/**
 * Forecast-by-category (MGR-02) — the per-rep endpoint rows are rolled up to one
 * row per Forecast_Position__c across the manager's team. Rendered via
 * MainTableTile as a TABLE (UI-SPEC: variance is a precise number). Verbatim
 * picklist labels; the null-position row is RETAINED and labeled "(no position)".
 */
function TeamForecastByCategoryTile({ active }: MainTeamTabProps): React.JSX.Element {
  const { data, isLoading, isError, error, refetch } = useTeamForecastByCategory({ active });

  let grouped: MainGroupedResponse | undefined;
  if (data) {
    // Roll up per-(rep, position) rows into one bucket per position. amount is
    // summed; a bucket stays null only when every contributing row is null.
    const byPosition = new Map<string, { label: string; value: number | null; count: number; sort: number }>();
    for (const row of data.rows) {
      const isNull = row.forecastPosition === null;
      const label = isNull ? NO_POSITION_LABEL : row.forecastPosition ?? NO_POSITION_LABEL;
      const orderIdx = FORECAST_POSITION_ORDER.indexOf(label);
      // null-position always last; unknown values after the known set but before null.
      const sort = isNull ? 999 : orderIdx === -1 ? 500 : orderIdx;
      const entry = byPosition.get(label) ?? { label, value: null, count: 0, sort };
      if (row.amount !== null) entry.value = (entry.value ?? 0) + row.amount;
      entry.count += row.oppCount;
      byPosition.set(label, entry);
    }
    const sorted = [...byPosition.values()].sort((a, b) => a.sort - b.sort);
    // exactOptionalPropertyTypes: omit `value` entirely (rather than set it to
    // undefined) when a bucket has no amount → MainTableTile renders the em-dash.
    const buckets = sorted.map((b) =>
      b.value === null
        ? { label: b.label, count: b.count }
        : { label: b.label, value: b.value, count: b.count },
    );

    grouped = {
      metric_id: 'MGR-02',
      period_label: 'This fiscal quarter',
      buckets,
      total_value: sorted.reduce((acc, b) => acc + (b.value ?? 0), 0),
      target_value: null,
      confidence: 'high',
      placeholder_text: null,
      // MainTableTile does not render freshness — this is an inert, type-valid
      // FreshnessInfo so the MainGroupedResponse shape typechecks.
      freshness: {
        status: 'cached',
        lastRefreshedAt: new Date().toISOString(),
        ageSeconds: 0,
        tier: 'fresh',
      },
    };
  }

  return (
    <MainTableTile
      metricId="MGR-02"
      title="Forecast by Category"
      tooltipFormula="Open Product pipeline this fiscal quarter grouped by the Forecast_Position__c field. The (no position) row is deals with no forecast position set."
      columns={FORECAST_COLUMNS}
      data={grouped}
      isLoading={isLoading}
      isError={isError}
      error={error}
      refetch={() => { void refetch(); }}
    />
  );
}

// ─── MGR-03: Win-rate / ACV / cycle-time by rep ──────────────────────────────

interface PerfRow {
  OwnerId: string;
  ownerName: string;
  winRate: number | null;
  avgAcv: number | null;
  avgCycleDays: number | null;
}

/**
 * Per-rep performance (MGR-03) — win-rate, ACV, and average cycle-time merged by
 * OwnerId from the winrate-by-rep + cycle-acv-by-rep endpoints. Rendered as a
 * bespoke Card table (rather than MainTableTile) because a footer TOTAL row would
 * be nonsensical here — you cannot sum win rates or cycle days. Null values render
 * as an em-dash, never a fabricated 0 (Pitfall 5).
 */
function TeamPerformanceTile({ active }: MainTeamTabProps): React.JSX.Element {
  const winrate = useTeamWinrateByRep({ active });
  const cycle = useTeamCycleAcvByRep({ active });

  const isLoading = winrate.isLoading || cycle.isLoading;
  const sfError =
    (winrate.isError && winrate.error instanceof SalesforceUnavailableError) ||
    (cycle.isError && cycle.error instanceof SalesforceUnavailableError);

  const perfHeader = (
    <CardHeader>
      <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
        Win Rate / ACV / Cycle by Rep
      </CardTitle>
    </CardHeader>
  );

  if (sfError) {
    return (
      <Card data-testid="main-tile-MGR-03">
        {perfHeader}
        <div className="p-4">
          <SfUnreachableBanner
            onRetry={() => {
              void winrate.refetch();
              void cycle.refetch();
            }}
          />
        </div>
      </Card>
    );
  }

  if (isLoading || !winrate.data || !cycle.data) {
    return (
      <Card data-testid="main-tile-MGR-03">
        {perfHeader}
        <div className="p-4 space-y-3" aria-busy="true">
          <Skeleton className="h-4 w-full" data-testid="skeleton-row-1" />
          <Skeleton className="h-4 w-full" data-testid="skeleton-row-2" />
          <Skeleton className="h-4 w-full" data-testid="skeleton-row-3" />
        </div>
      </Card>
    );
  }

  // Merge the two endpoints by OwnerId.
  const byRep = new Map<string, PerfRow>();
  for (const w of winrate.data.rows) {
    byRep.set(w.OwnerId, {
      OwnerId: w.OwnerId,
      ownerName: w.ownerName,
      winRate: w.winRate,
      avgAcv: null,
      avgCycleDays: null,
    });
  }
  for (const c of cycle.data.rows) {
    const entry =
      byRep.get(c.OwnerId) ??
      { OwnerId: c.OwnerId, ownerName: c.ownerName, winRate: null, avgAcv: null, avgCycleDays: null };
    entry.avgAcv = c.avgAcv;
    entry.avgCycleDays = c.avgCycleDays;
    byRep.set(c.OwnerId, entry);
  }
  const perfRows = [...byRep.values()].sort((a, b) => a.ownerName.localeCompare(b.ownerName));

  return (
    <Card data-testid="main-tile-MGR-03">
      {perfHeader}

      {perfRows.length === 0 && (
        <p
          className="py-12 text-center text-sm text-ink-500"
          data-testid="team-perf-empty-state"
        >
          No reps in view — you&apos;re seeing only your own team, per Salesforce
          sharing rules.
        </p>
      )}

      {perfRows.length > 0 && (
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white border-b border-ink-200">
              <tr>
                <th className="px-4 py-2 text-left text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                  REP
                </th>
                <th className="px-4 py-2 text-right text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                  WIN RATE
                </th>
                <th className="px-4 py-2 text-right text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                  ACV
                </th>
                <th className="px-4 py-2 text-right text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                  AVG CYCLE (DAYS)
                </th>
              </tr>
            </thead>
            <tbody>
              {perfRows.map((row) => (
                <tr
                  key={row.OwnerId}
                  className="min-h-9 hover:bg-ink-100 border-b border-ink-100"
                  data-testid="team-perf-row"
                >
                  <td className="px-4 py-3 text-ink-900">{row.ownerName}</td>
                  <td className="px-4 py-3 text-ink-900 tabular-nums text-right">
                    {row.winRate === null ? EM_DASH : `${String(Math.round(row.winRate * 100))}%`}
                  </td>
                  <td className="px-4 py-3 text-ink-900 tabular-nums text-right">
                    {row.avgAcv === null ? EM_DASH : formatAmount(row.avgAcv)}
                  </td>
                  <td className="px-4 py-3 text-ink-900 tabular-nums text-right">
                    {row.avgCycleDays === null ? EM_DASH : String(Math.round(row.avgCycleDays))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
